import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Upload, UserRound, Pencil, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { nom: "", poste: "", photo: null, categorie: "dirigeant", ordre: 0 };

export default function AdminStaff() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/staff`).then((r) => setItems(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, []);
  useEffect(load, [load]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, { withCredentials: true });
      setForm((f) => ({ ...f, photo: data.url }));
      toast.success("Photo téléversée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du téléversement");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, ordre: +form.ordre || 0 };
      if (editing) {
        await axios.put(`${API}/admin/staff/${editing}`, payload, { withCredentials: true });
        toast.success("Membre mis à jour");
      } else {
        await axios.post(`${API}/admin/staff`, payload, { withCredentials: true });
        toast.success("Membre ajouté");
      }
      setForm(empty);
      setEditing(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const edit = (m) => {
    setForm({ nom: m.nom, poste: m.poste, photo: m.photo, categorie: m.categorie, ordre: m.ordre });
    setEditing(m.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce membre ?")) return;
    await axios.delete(`${API}/admin/staff/${id}`, { withCredentials: true });
    toast.success("Membre supprimé");
    load();
  };

  const dirigeants = items.filter((m) => m.categorie === "dirigeant");
  const employes = items.filter((m) => m.categorie === "employe");

  return (
    <div data-testid="admin-staff">
      <form onSubmit={save} className="border border-[#27272A] bg-[#121214] p-6 mb-10" data-testid="staff-form">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-5">
          {editing ? "Modifier le membre" : "Ajouter un membre"}
        </p>
        <div className="grid sm:grid-cols-2 gap-5">
          <input required placeholder="Nom complet *" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="staff-input-nom"
            className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
          <input required placeholder="Poste / fonction * (ex: Directeur Général)" value={form.poste} onChange={(e) => setForm({ ...form, poste: e.target.value })} data-testid="staff-input-poste"
            className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
          <select value={form.categorie} onChange={(e) => setForm({ ...form, categorie: e.target.value })} data-testid="staff-select-categorie"
            className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]">
            <option value="dirigeant">Dirigeant</option>
            <option value="employe">Employé</option>
          </select>
          <input type="number" min="0" placeholder="Ordre d'affichage (0 = premier)" value={form.ordre} onChange={(e) => setForm({ ...form, ordre: e.target.value })} data-testid="staff-input-ordre"
            className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
        </div>
        <div className="flex flex-wrap items-center gap-5 mt-5">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadPhoto} className="hidden" data-testid="staff-photo-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="staff-photo-button"
            className="flex items-center gap-2 border border-[#27272A] px-5 py-3 text-xs uppercase tracking-[0.2em] text-[#A1A1AA] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors disabled:opacity-50">
            <Upload size={14} /> {uploading ? "Envoi…" : form.photo ? "Changer la photo" : "Téléverser une photo"}
          </button>
          {form.photo && <img src={form.photo} alt="Aperçu" className="h-16 w-12 object-cover border border-[#27272A]" data-testid="staff-photo-preview" />}
        </div>
        <div className="flex gap-4 mt-6">
          <button type="submit" data-testid="staff-save-button"
            className="bg-[#C5A059] text-black px-8 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors">
            {editing ? "Mettre à jour" : "Ajouter"}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(empty); }} data-testid="staff-cancel-edit"
              className="border border-[#27272A] px-6 py-3 text-xs uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-white transition-colors">
              Annuler
            </button>
          )}
        </div>
      </form>

      {[["Dirigeants", dirigeants, "dir"], ["Employés", employes, "emp"]].map(([label, list, key]) => (
        <div key={key} className="mb-10">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-4">{label} · {list.length}</p>
          {list.length === 0 ? (
            <p className="text-sm text-[#3f3f46] border border-[#27272A] p-6">Aucun membre.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map((m) => (
                <div key={m.id} data-testid={`admin-staff-row-${m.id.slice(0, 8)}`} className="border border-[#27272A] bg-[#121214] p-4 flex items-center gap-4">
                  {m.photo ? (
                    <img src={m.photo} alt={m.nom} className="h-14 w-11 object-cover border border-[#27272A]" />
                  ) : (
                    <div className="h-14 w-11 bg-[#0A0A0C] border border-[#27272A] flex items-center justify-center"><UserRound size={18} className="text-[#3f3f46]" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{m.nom}</p>
                    <p className="text-[10px] uppercase tracking-widest text-[#C5A059] truncate">{m.poste}</p>
                  </div>
                  <button onClick={() => edit(m)} data-testid={`staff-edit-${m.id.slice(0, 8)}`} className="text-[#71717A] hover:text-[#C5A059] transition-colors" aria-label="Modifier"><Pencil size={15} /></button>
                  <button onClick={() => remove(m.id)} data-testid={`staff-delete-${m.id.slice(0, 8)}`} className="text-[#71717A] hover:text-red-400 transition-colors" aria-label="Supprimer"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
